/* eslint-disable react/jsx-props-no-spreading */
/**
 * @description Renders the Knowledge Dialog Form
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import pick from 'lodash/pick';

import Spinner from '@seccon/shared/ui/components/Spinner';

import {
  CATEGORIES,
  ROLES,
  MASTER_ROLE,
  ADMIN_MODE
} from '../constants';
import useKnowledgeEntry from '../hooks/useKnowledgeEntry';

// Convert the category list into a list of options
const CATEGORY_OPTIONS = CATEGORIES.map((entry) => pick(entry, ['label', 'value']));

const styles = {
  allRoles: {
    m: 0
  },
  button: {
    minHeight: '2.25rem',
    minWidth: '6.25rem'
  },
  rolesContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1
  },
  roles: {
    flex: 1
  }
};

/**
 * Retrieve the iframe source from the html string
 * @param {string} htmlString - html string to be parsed
 * @returns {string} iframe src attribute
 */
function getIframeSrc (htmlString) {
  let iframeSrc = '';

  try {
    // Parse the html string into DOM elements
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Find the iframe element and retrieve its src attribute
    iframeSrc = doc.querySelector('iframe').getAttribute('src').trim() ?? '';
  } catch (err) {
    /* do nothing */
  }

  return iframeSrc;
}

/**
 * A validation function that determines if the html string entered has an iframe source or not
 * @param {string} htmlString - html string to be parsed
 * @returns {string} - error message to be displayed if it has no iframe source
 */
function hasIframeSrc (htmlString) {
  return getIframeSrc(htmlString) === '' ? 'Invalid iframe source' : null;
}

/**
 * @prop {string} knowledgeId - indicates the knowledge entry id to be loaded in the dialog
 * @prop {string} mode - indicates which mode the dialog is in
 * @prop {() => void} onClose - callback function when the dialog should be closed
 * @prop {(data: object) => void} onSubmit - callback function when the dialog form is submitted. It takes the form data as
 *  a parameter
 */
const KnowledgeDialogForm = ({
  knowledgeId,
  mode,
  onClose,
  onSubmit
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Set up the form
  const form = useForm({
    defaultValues: {
      roles: [MASTER_ROLE]
    },
    mode: 'onChange',
    shouldUnregister: true
  });

  // Determine if the form submit should be disabled because the form is prestine, invalid, or currently submitting
  const { isDirty, isValid } = form.formState;
  const disabled = !isDirty || !isValid || isSubmitting;

  // Retrieve the initial form data and loading state if a knowledge id was provided
  const { data: initialData, isLoading, refetch } = useKnowledgeEntry(knowledgeId);

  // If the knowledge id changes, refetch the form data
  React.useEffect(() => {
    if (knowledgeId) {
      refetch({ throwOnError: true })
        // On failure, close the dialog
        .catch(() => onClose());
    }
  }, [knowledgeId]);

  // If the initial form data changes, reset the form state
  React.useEffect(() => {
    if (initialData) {
      form.reset(initialData);
    }
  }, [initialData]);

  /**
   * Handles form submission by calling the onSubmit handler
   * @param {{ category: string, roles: string[], embedSrc: string }} data - form data
   */
  function handleSubmit (data) {
    setIsSubmitting(true);

    onSubmit({
      category: data.category.value,
      roles: data.roles.map((entry) => entry.value),
      embedSrc: getIframeSrc(data.embedSrc)
    // Regardless of submit success or failure, reset the form submitting state
    }).finally(() => setIsSubmitting(false));
  }

  // Set the action button label based on the form mode
  const actionBtnLabel = mode === ADMIN_MODE.EDIT ? 'Update' : 'Add';

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <DialogContent>
        {isLoading ? (
          <>
            <Skeleton width='100%' height='4rem' />
            <Skeleton width='100%' height='4rem' />
            <Skeleton width='100%' height='4rem' />
          </>
        ) : (
          <Stack spacing={2}>
            <Controller
              control={form.control}
              name='category'
              render={({ field: { onChange, value }, fieldState }) => {
                const { error } = fieldState;

                return (
                  <Autocomplete
                    autoHighlight
                    isOptionEqualToValue={(option) => option.value === value.value}
                    options={CATEGORY_OPTIONS}
                    onChange={(_, selection) => onChange(selection)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoFocus
                        helperText={error ? error.message : ''}
                        label='Category'
                        required
                        size='small'
                      />
                    )}
                    value={value ?? null}
                  />
                );
              }}
              rules={{ required: true }}
            />
            <Controller
              control={form.control}
              defaultValue={[]}
              name='roles'
              render={({ field: { onChange, value }, fieldState }) => {
                const { error } = fieldState;
                const selections = value.map((entry) => entry.value);
                const isAllChecked = ROLES.every((role) => value.findIndex((val) => val.value === role.value) >= 0);

                return (
                  <Box sx={styles.rolesContainer}>
                    <Autocomplete
                      autoHighlight
                      filterSelectedOptions
                      isOptionEqualToValue={(option) => selections.includes(option.value)}
                      multiple
                      options={ROLES}
                      onChange={(_, newValue) => {
                        onChange([
                          MASTER_ROLE,
                          ...newValue.filter((option) => option.value !== MASTER_ROLE.value)
                        ])
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          helperText={error ? error.message : ''}
                          inputProps={{
                            ...params.inputProps,
                            // Necessary because otherwise TextField thinks a required field is empty, even when values are selected
                            // Only necessary because Autocomplete is set to "multiple"
                            required: value.length === 0
                          }}
                          label='Role'
                          required
                          size='small'
                        />
                      )}
                      renderTags={(tagValue, getTagProps) => (
                        tagValue.map((option, index) => (
                          <Chip
                            label={option.label}
                            {...getTagProps({ index })}
                            disabled={option.value === MASTER_ROLE.value}
                          />
                        ))
                      )}
                      sx={styles.roles}
                      value={value ?? []}
                    />
                    <FormControlLabel
                      checked={isAllChecked}
                      control={(
                        <Checkbox />
                      )}
                      label='All'
                      onChange={(evt) => (evt.target.checked ? onChange(ROLES) : onChange([MASTER_ROLE]))}
                      sx={styles.allRoles}
                    />
                  </Box>
                );
              }}
              rules={{ required: true }}
            />
            <Controller
              control={form.control}
              name='embedSrc'
              render={({ field: { onChange, value }, fieldState }) => {
                const { error } = fieldState;

                return (
                  <TextField
                    error={Boolean(error)}
                    onChange={(val) => onChange(val)}
                    helperText={error ? error.message : ''}
                    label='IFrame Tag'
                    multiline
                    required
                    value={value ?? ''}
                  />
                );
              }}
              rules={{ required: true, validate: hasIframeSrc }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} sx={styles.button} variant='outlined'>Cancel</Button>
        <Button disabled={isLoading || disabled} sx={styles.button} type='submit' variant='contained'>
          {isSubmitting ? <Spinner /> : actionBtnLabel}
        </Button>
      </DialogActions>
    </form>
  );
};

KnowledgeDialogForm.propTypes = {
  mode: PropTypes.string,
  knowledgeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

KnowledgeDialogForm.defaultProps = {
  mode: ADMIN_MODE.ADD,
  knowledgeId: null
};

export default KnowledgeDialogForm;
